import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Spin, message, Card, Row, Col, Select, Checkbox, InputNumber } from 'antd';
import { Helmet } from 'react-helmet-async';
import { fetchShopById, updateShop } from '../services/shopService';
import OpeningHoursEditor from '../components/OpeningHoursEditor';
import ImageUpload from '../components/ImageUpload';
import { geocodeAddress } from '../services/mapService';
import './EditShopPage.css';

const { TextArea } = Input;
const { Option } = Select;

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const EditShopPage = () => {
    const { id: shopId } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [shop, setShop] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [error, setError] = useState(null);
    const [currentImages, setCurrentImages] = useState([]);
    const [openingHoursState, setOpeningHoursState] = useState({});

    const fetchShopData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchShopById(shopId);
            if (response.success && response.data) {
                const shopData = response.data;
                setShop(shopData);
                form.setFieldsValue({
                    name: shopData.name,
                    ownerName: shopData.ownerName,
                    email: shopData.email,
                    phone: shopData.phone,
                    address: shopData.address,
                    postalCode: shopData.postalCode,
                    city: shopData.city,
                    website: shopData.website || '',
                    description: shopData.description,
                    brands: shopData.brands || [],
                    isActive: shopData.isActive !== undefined ? shopData.isActive : true,
                });
                setCurrentImages(shopData.images || []);

                // Konvertiere das Array aus der API in das Objektformat für den State
                const hoursArray = Array.isArray(shopData.openingHours) ? shopData.openingHours : [];
                const hoursObject = hoursArray.reduce((acc, item) => {
                    // Stelle sicher, dass dayOfWeek existiert und ein gültiger String ist
                    if (item && typeof item.dayOfWeek === 'string') {
                        // Verwende den Grossbuchstaben-Tag aus dem Schema
                        acc[item.dayOfWeek] = {
                            opens: item.opens || '',
                            closes: item.closes || ''
                        };
                    }
                    return acc;
                }, {});
                setOpeningHoursState(hoursObject); // Initialisiere State mit dem Objekt

            } else {
                throw new Error(response.message || 'Shop konnte nicht geladen werden.');
            }
        } catch (err) {
            console.error("Fehler beim Laden des Shops:", err);
            setError(err.message || 'Ein Fehler ist aufgetreten.');
            message.error(`Shop konnte nicht geladen werden: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchShopData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shopId, form]);

    const handleUpdate = async (values) => {
        setUpdating(true);
        setError(null);
        message.loading({ content: 'Shop wird aktualisiert...', key: 'updateShop' });

        try {
            const addressChanged = shop.address !== values.address ||
                                 shop.postalCode !== values.postalCode ||
                                 shop.city !== values.city;

            let newCoordinates = shop.coordinates;

            if (addressChanged) {
                message.info({ content: 'Adresse geändert, versuche Geokodierung...', key: 'geocode', duration: 2 });
                const fullAddress = `${values.address}, ${values.postalCode} ${values.city}, Deutschland`;
                try {
                    const geocodedResult = await geocodeAddress(fullAddress);
                    if (geocodedResult && (geocodedResult.lat !== 0 || geocodedResult.lng !== 0)) {
                        newCoordinates = geocodedResult;
                        message.success({ content: 'Adresse erfolgreich geokodiert.', key: 'geocode', duration: 2 });
                        console.log("Neue Koordinaten:", newCoordinates);
                    } else {
                        message.warning({ content: 'Neue Adresse konnte nicht automatisch geokodiert werden. Alte Koordinaten bleiben erhalten.', key: 'geocode', duration: 5 });
                    }
                } catch (geoError) {
                    console.error("Fehler bei der Geokodierung:", geoError);
                    message.error({ content: 'Fehler bei der Geokodierung. Alte Koordinaten bleiben erhalten.', key: 'geocode', duration: 5 });
                }
            }

            // Öffnungszeiten-Objekt in das vom Backend erwartete Array-Format umwandeln
            const formattedOpeningHours = Object.entries(openingHoursState)
                .map(([day, hours]) => ({
                    dayOfWeek: day, // Frontend verwendet bereits korrekte Grossschreibung
                    opens: hours.opens || '', // Stelle sicher, dass es Strings sind
                    closes: hours.closes || ''
                }));

            const updatedData = {
                ...values,
                openingHours: formattedOpeningHours, // Formatiertes Array senden
                images: currentImages,
                coordinates: newCoordinates
            };

            delete updatedData.user;
            delete updatedData.subscriptionPlan;
            delete updatedData.subscriptionStatus;
            delete updatedData.slug;
            delete updatedData.rating;
            delete updatedData.reviewCount;

            const response = await updateShop(shopId, updatedData);

            if (response.success) {
                message.success({ content: 'Shop erfolgreich aktualisiert!', key: 'updateShop', duration: 2 });
                setShop(response.data);
                navigate('/dashboard', { replace: true });
            } else {
                throw new Error(response.message || 'Fehler beim Aktualisieren des Shops.');
            }
        } catch (err) {
            console.error("Fehler beim Aktualisieren des Shops:", err);
            setError(err.message || 'Ein Update-Fehler ist aufgetreten.');
            message.error({ content: `Fehler: ${err.message || 'Unbekannter Fehler'}`, key: 'updateShop', duration: 5 });
        } finally {
            setUpdating(false);
        }
    };

    const handleImageChange = (newImageList) => {
        console.log("Neue Bildliste vom Uploader:", newImageList);
        setCurrentImages(newImageList);
    };

    if (loading) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><Spin size="large" tip="Lade Shop-Daten..." /></div>;
    }

    if (error && !shop) {
        return <div className="container py-5"><Card><p style={{color: 'red'}}>Fehler beim Laden der Shop-Daten: {error}</p><Button onClick={() => navigate('/dashboard')}>Zum Dashboard</Button></Card></div>;
    }

    if (!shop) {
        return <div className="container py-5"><Card><p>Shop nicht gefunden.</p><Button onClick={() => navigate('/dashboard')}>Zum Dashboard</Button></Card></div>;
    }

    return (
        <div className="container py-5 edit-shop-page">
            <Helmet>
                <title>Shop Bearbeiten | {shop ? shop.name : 'Shop'} | Snus Finder</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            <Card title={`Shop bearbeiten: ${shop ? shop.name : 'Unbekannt'}`}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleUpdate}
                    initialValues={{
                        isActive: true,
                        brands: [],
                    }}
                >
                    <Row gutter={24}>
                        <Col xs={24} md={12}>
                            <Form.Item name="name" label="Shop Name" rules={[{ required: true, message: 'Bitte Shop-Namen eingeben!' }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="ownerName" label="Inhaber Name" rules={[{ required: true, message: 'Bitte Namen des Inhabers eingeben!' }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col xs={24} md={12}>
                            <Form.Item name="email" label="Kontakt E-Mail" rules={[{ required: true, type: 'email', message: 'Bitte gültige E-Mail eingeben!' }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="phone" label="Telefonnummer" rules={[{ required: true, message: 'Bitte Telefonnummer eingeben!' }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col xs={24} md={12}>
                            <Form.Item name="address" label="Straße & Hausnummer" rules={[{ required: true, message: 'Bitte Adresse eingeben!' }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Item name="postalCode" label="PLZ" rules={[{ required: true, pattern: /^[0-9]{5}$/, message: 'Bitte gültige PLZ (5 Ziffern) eingeben!' }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Item name="city" label="Stadt" rules={[{ required: true, message: 'Bitte Stadt eingeben!' }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="website" label="Webseite (optional)">
                        <Input placeholder="https://beispiel.de" />
                    </Form.Item>

                    <Form.Item name="description" label="Beschreibung" rules={[{ required: true, message: 'Bitte Beschreibung eingeben!' }]}>
                        <TextArea rows={4} />
                    </Form.Item>

                    <Form.Item name="brands" label="Angebotene Marken">
                         <Select
                            mode="tags"
                            style={{ width: '100%' }}
                            placeholder="Marken hinzufügen (z.B. Siberia, Odens) und Enter drücken"
                            tokenSeparators={[',']}
                            notFoundContent={null}
                         />
                    </Form.Item>

                    <Form.Item label="Öffnungszeiten">
                      <OpeningHoursEditor
                          value={openingHoursState}
                          onChange={setOpeningHoursState}
                      />
                    </Form.Item>

                    <Form.Item label="Bilder">
                        <Card size="small" title="Bildverwaltung">
                            <ImageUpload
                                initialImageList={currentImages}
                                onChange={handleImageChange}
                                maxImages={8}
                            />
                            <p style={{ marginTop: '8px', fontSize: '12px', color: 'gray' }}>
                                Maximal 8 Bilder erlaubt.
                                Bildgröße bis zu 5MB (JPG, PNG, WEBP).
                            </p>
                        </Card>
                    </Form.Item>

                    <Form.Item name="isActive" valuePropName="checked">
                        <Checkbox>Dieser Shop ist öffentlich sichtbar</Checkbox>
                    </Form.Item>

                    {error && <p style={{ color: 'red' }}>Update-Fehler: {error}</p>}

                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={updating} disabled={loading}>
                            Änderungen Speichern
                        </Button>
                        <Button style={{ marginLeft: 8 }} onClick={() => navigate('/dashboard')} disabled={updating}>
                            Abbrechen
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default EditShopPage;